// src/pages/CashDrawer.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCashDrawer,
  listCashDrawerHistory,
  updateBeginCash,
  updateDrawerCount,
} from "../api/cashDrawer";
import { useAuth } from "../context/AuthContext";
import { getSalonTimezone } from "../utils/salonTimezone";
import ModalCloseX from "../components/common/ModalCloseX";

const CENTS_KEYS = [
  "10000",
  "5000",
  "2000",
  "1000",
  "500",
  "200",
  "100",
  "25",
  "10",
  "5",
];

const DENOM_LABELS = {
  "10000": "$100",
  "5000": "$50",
  "2000": "$20",
  "1000": "$10",
  "500": "$5",
  "200": "$2",
  "100": "$1",
  "25": "25c",
  "10": "10c",
  "5": "5c",
};

const buildEmptyDenoms = () =>
  CENTS_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

const normalizeDenoms = (source) => {
  const base = buildEmptyDenoms();
  if (!source) return base;
  CENTS_KEYS.forEach((key) => {
    const raw = source[key];
    const parsed = Number.isFinite(Number(raw)) ? Number(raw) : 0;
    base[key] = Math.max(0, Math.floor(parsed));
  });
  return base;
};

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateFromParts = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const addDaysToIsoDate = (dateStr, deltaDays) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateFromParts(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate()
  );
};

const getWeekdayIndex = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return null;
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
};

const toIsoDate = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (raw.length >= 10 && raw[4] === "-" && raw[7] === "-") {
    return raw.slice(0, 10);
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return "";
};

const sortDescByWorkDate = (rows) =>
  [...rows].sort((a, b) =>
    String(b.work_date).localeCompare(String(a.work_date))
  );

const normalizeHistoryRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const mapped = list
    .map((row) => ({
      id: row?.id ?? null,
      work_date: toIsoDate(row?.work_date ?? row?.date),
      begin_amount: Number(row?.begin_amount ?? row?.begin ?? 0) || 0,
      in_drawer_amount:
        Number(row?.in_drawer_amount ?? row?.drawer ?? 0) || 0,
    }))
    .filter((row) => row.work_date);
  const seen = new Set();
  const deduped = [];
  for (const row of mapped) {
    if (seen.has(row.work_date)) continue;
    seen.add(row.work_date);
    deduped.push(row);
  }
  return sortDescByWorkDate(deduped);
};

const formatDisplayDate = (value, timeZone) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }).format(parsed);
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  return parsed.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "$0.00";
  return `$${number.toFixed(2)}`;
};

const denomsToCents = (denoms) =>
  CENTS_KEYS.reduce((sum, key) => {
    const count = Number(denoms?.[key] ?? 0);
    return sum + Math.max(0, Math.floor(count)) * Number(key);
  }, 0);

const centsToMoney = (cents) => (cents / 100).toFixed(2);

function DenominationEditor({
  denoms,
  setDenoms,
  readonlyTotal,
  showTotalRow = true,
  compact = false,
}) {
  const fieldClass = compact
    ? "h-9 px-2 text-sm md:text-base rounded-lg border border-slate-300"
    : "h-10 md:h-11 px-3 text-base md:text-lg rounded-xl border border-slate-300";
  const listClass = compact ? "space-y-1" : "space-y-2";
  const rowClass = compact
    ? "grid grid-cols-12 items-center gap-2"
    : "grid grid-cols-12 items-center gap-3";
  const labelClass = compact
    ? "col-span-4 text-sky-700 text-sm md:text-base font-semibold"
    : "col-span-4 text-sky-700 font-semibold";
  const valueClass = compact
    ? "col-span-4 text-right text-slate-700 text-sm md:text-base"
    : "col-span-4 text-right text-slate-700";
  const totalLabelClass = compact
    ? "text-sm md:text-base font-semibold text-slate-900"
    : "text-base md:text-lg font-semibold text-slate-900";
  const totalWrapperClass = compact
    ? "mt-3 border-t border-slate-200 pt-2 flex items-center justify-between"
    : "mt-4 border-t border-slate-200 pt-3 flex items-center justify-between";
  return (
    <div className={listClass}>
      {CENTS_KEYS.map((key) => {
        const count = Number(denoms?.[key] ?? 0);
        const rowCents = Math.max(0, Math.floor(count)) * Number(key);
        return (
          <div
            key={key}
            className={rowClass}
          >
            <div className={labelClass}>
              {DENOM_LABELS[key]}
            </div>
            <input
              type="number"
              min="0"
              value={Number.isFinite(count) ? count : 0}
              onChange={(event) => {
                const raw = event.target.value;
                const nextVal = raw === "" ? 0 : Math.max(0, Number(raw));
                setDenoms((prev) => ({
                  ...prev,
                  [key]: Math.floor(nextVal),
                }));
              }}
              className={`col-span-4 ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
            />
            <div className={valueClass}>
              {centsToMoney(rowCents)}
            </div>
          </div>
        );
      })}
      {showTotalRow && (
        <div className={totalWrapperClass}>
          <span className={totalLabelClass}>
            In Drawer
          </span>
          <span className={totalLabelClass}>
            {formatMoney(readonlyTotal)}
          </span>
        </div>
      )}
    </div>
  );
}

export default function CashDrawer() {
  const navigate = useNavigate();
  const { user, currentSalon } = useAuth();
  const salonId = user?.salon_id ?? null;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const todayStr = useMemo(
    () => formatDateISO(new Date(), salonTimezone),
    [salonTimezone]
  );
  const [drawer, setDrawer] = useState(null);
  const [countDenoms, setCountDenoms] = useState(buildEmptyDenoms());
  const [beginDenoms, setBeginDenoms] = useState(buildEmptyDenoms());
  const [beginOpen, setBeginOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyRange, setHistoryRange] = useState({
    start: "",
    end: todayStr,
  });
  const [viewingHistoryDate, setViewingHistoryDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const didInitRef = useRef(false);
  const historyLoadingRef = useRef(false);
  const drawerLoadingRef = useRef(false);
  const lastDrawerKeyRef = useRef("");
  const lastHistoryKeyRef = useRef("");

  const inDrawerCents = useMemo(
    () => denomsToCents(countDenoms),
    [countDenoms]
  );
  const beginDrawerCents = useMemo(
    () => denomsToCents(beginDenoms),
    [beginDenoms]
  );

  const loadDrawer = useCallback(
    async (dateValue, { force = false } = {}) => {
      if (!salonId || !dateValue) return;
      const dateIso = toIsoDate(dateValue);
      if (!dateIso) return;
      const key = `${salonId}|${dateIso}`;
      if (!force && lastDrawerKeyRef.current === key) return;
      if (drawerLoadingRef.current) return;
      lastDrawerKeyRef.current = key;
      drawerLoadingRef.current = true;
      try {
        const data = await getCashDrawer({ date: dateIso, salon_id: salonId });
        setDrawer(data);
      } finally {
        drawerLoadingRef.current = false;
      }
    },
    [salonId]
  );

  const loadHistory = useCallback(
    async ({ start, end, force = false } = {}) => {
      const startIso = toIsoDate(start ?? historyRange.start);
      const endIso = toIsoDate(end ?? historyRange.end);
      if (!salonId || !startIso || !endIso) return;
      const key = `${salonId}|${startIso}|${endIso}`;
      if (!force && lastHistoryKeyRef.current === key) return;
      if (historyLoadingRef.current) return;
      lastHistoryKeyRef.current = key;
      historyLoadingRef.current = true;
      try {
        const data = await listCashDrawerHistory({
          start: startIso,
          end: endIso,
          salon_id: salonId,
        });
        const normalized = normalizeHistoryRows(data);
        setHistoryRows(normalized);
      } finally {
        historyLoadingRef.current = false;
      }
    },
    [historyRange.end, historyRange.start, salonId]
  );

  const refreshHistory = useCallback(async () => {
    if (!historyRange.end) return;
    await loadHistory({
      start: historyRange.start,
      end: historyRange.end,
      force: true,
    });
  }, [historyRange.end, historyRange.start, loadHistory]);

  const setRangeAndFetch = useCallback(
    async (start, end) => {
      const startIso = toIsoDate(start);
      const endIso = toIsoDate(end);
      setHistoryRange({ start: startIso, end: endIso });
      await loadHistory({ start: startIso, end: endIso, force: true });
    },
    [loadHistory]
  );

  useEffect(() => {
    if (!salonId || didInitRef.current) return;
    didInitRef.current = true;
    setSelectedDate(todayStr);
    setHistoryRange({ start: todayStr, end: todayStr });
    loadHistory({ start: todayStr, end: todayStr, force: true });
  }, [salonId, todayStr, loadHistory]);

  useEffect(() => {
    if (!selectedDate) return;
    loadDrawer(selectedDate);
  }, [selectedDate, loadDrawer]);

  useEffect(() => {
    const handleDrawerRefresh = (event) => {
      const detail = event?.detail || {};
      if (detail.salonId && salonId && Number(detail.salonId) !== Number(salonId)) {
        return;
      }
      const eventDate = toIsoDate(detail.date);
      const currentDate = toIsoDate(selectedDate || todayStr);
      if (eventDate && currentDate && eventDate !== currentDate) {
        return;
      }
      loadDrawer(currentDate || selectedDate || todayStr, { force: true });
    };

    window.addEventListener("cashDrawer:refresh", handleDrawerRefresh);
    return () => {
      window.removeEventListener("cashDrawer:refresh", handleDrawerRefresh);
    };
  }, [loadDrawer, salonId, selectedDate, todayStr]);

  useEffect(() => {
    if (!drawer) return;
    if (drawer?.in_drawer_denoms) {
      setCountDenoms(normalizeDenoms(drawer.in_drawer_denoms));
    } else {
      setCountDenoms(normalizeDenoms(drawer?.begin_denoms));
    }
  }, [drawer]);

  useEffect(() => {
    if (!beginOpen) return;
    setBeginDenoms(normalizeDenoms(drawer?.begin_denoms));
  }, [beginOpen, drawer]);

  const handleSaveDrawer = async () => {
    if (!drawer?.id) return;
    const data = await updateDrawerCount(drawer.id, countDenoms);
    setDrawer(data);
    setCountDenoms(normalizeDenoms(data?.in_drawer_denoms));
    setHistoryRows((prev) =>
      prev.map((row) =>
        row.work_date === data?.work_date
          ? {
            ...row,
            begin_amount: data?.begin_amount,
            in_drawer_amount: data?.in_drawer_amount,
          }
          : row
      )
    );
    await refreshHistory();
  };

  const handleUpdateBegin = async () => {
    if (!drawer?.id) return;
    const data = await updateBeginCash(drawer.id, beginDenoms);
    setDrawer(data);
    setCountDenoms(normalizeDenoms(data?.in_drawer_denoms));
    setBeginOpen(false);
    setHistoryRows((prev) =>
      prev.map((row) =>
        row.work_date === data?.work_date
          ? {
            ...row,
            begin_amount: data?.begin_amount,
            in_drawer_amount: data?.in_drawer_amount,
          }
          : row
      )
    );
    await refreshHistory();
  };

  const handleHistorySearch = async () => {
    let { start, end } = historyRange;
    if (!start || !end) return;
    if (start > end) {
      [start, end] = [end, start];
      setHistoryRange({ start, end });
    }
    await loadHistory({ start, end, force: true });
  };

  const setTodayRange = async () => {
    await setRangeAndFetch(todayStr, todayStr);
  };

  const setWeekRange = async () => {
    const day = getWeekdayIndex(todayStr);
    if (day === null) return;
    const diffToMonday = (day + 6) % 7;
    const monday = addDaysToIsoDate(todayStr, -diffToMonday);
    const sunday = addDaysToIsoDate(monday, 6);
    await setRangeAndFetch(monday, sunday);
  };

  const setMonthRange = async () => {
    const year = Number(todayStr.slice(0, 4));
    const month = Number(todayStr.slice(5, 7));
    const endDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const start = formatDateFromParts(year, month, 1);
    const end = formatDateFromParts(year, month, endDay);
    await setRangeAndFetch(start, end);
  };

  const isViewingHistory =
    viewingHistoryDate && viewingHistoryDate !== todayStr;

  const titleDate = formatDisplayDate(
    drawer?.work_date || todayStr,
    salonTimezone
  );

  const modalText = "text-base md:text-lg";
  const labelText =
    "text-sm md:text-base font-semibold tracking-wide text-slate-500";
  const fieldClass =
    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300";
  const btnBase = "h-11 md:h-12 px-6 text-base md:text-lg rounded-full";
  const modalBtnBase = "h-10 md:h-11 px-5 text-sm md:text-base rounded-full";
  const EPS = 0.005;
  const overUnderAmount = Number(drawer?.over_under ?? 0);
  const tooltipOver =
    "Over means counted cash is MORE than expected.";
  const tooltipUnder =
    "Under means counted cash is LESS than expected.";
  const tooltipBalanced =
    "Balanced means counted cash matches expected cash.";
  let balanceLabel = "Balanced";
  let balanceDisplayAmount = formatMoney(0);
  let balanceTooltip = tooltipBalanced;

  if (overUnderAmount > EPS) {
    balanceLabel = "Over";
    balanceDisplayAmount = formatMoney(overUnderAmount);
    balanceTooltip = tooltipOver;
  } else if (overUnderAmount < -EPS) {
    balanceLabel = "Under";
    balanceDisplayAmount = `-${formatMoney(Math.abs(overUnderAmount))}`;
    balanceTooltip = tooltipUnder;
  }

  balanceTooltip =
    balanceLabel === "Over"
      ? tooltipOver
      : balanceLabel === "Under"
        ? tooltipUnder
        : tooltipBalanced;

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell min-h-screen bg-slate-100/60">
      <div className="manageSectionWide manage-section-narrow manage-section-compact">
        {isViewingHistory && (
          <button
            type="button"
            onClick={async () => {
              setViewingHistoryDate(null);
              setSelectedDate(todayStr);
            }}
            className="mb-4 inline-flex items-center rounded-full border border-slate-300 bg-white px-5 py-2 text-sm md:text-base font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Return to Current Cash Drawer
          </button>
        )}
        <div
          className={`rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${modalText}`}
        >
          <div className="flex items-center justify-between bg-rose-500 px-6 py-4 relative">
            <h2 className="text-xl md:text-2xl font-semibold text-white">
              Cash Drawer: {titleDate}
            </h2>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="p-6">
            <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-4 text-sm md:text-base font-semibold text-slate-500 uppercase tracking-wide">
                  Denominations
                </div>
                <DenominationEditor
                  denoms={countDenoms}
                  setDenoms={setCountDenoms}
                  readonlyTotal={Number(centsToMoney(inDrawerCents))}
                  showTotalRow
                />
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleSaveDrawer}
                    className={`${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800`}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setCountDenoms(buildEmptyDenoms())}
                    className={`${btnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-4 text-sm md:text-base font-semibold text-slate-500 uppercase tracking-wide">
                  Summary
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Begin Cash</span>
                    <button
                      type="button"
                      onClick={() => setBeginOpen(true)}
                      className="text-slate-900 font-semibold underline decoration-dotted underline-offset-4"
                    >
                      {formatMoney(drawer?.begin_amount)}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Paid In</span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(drawer?.paid_in)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Paid Out</span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(drawer?.paid_out)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Tendered</span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(drawer?.tendered ?? 0)}
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-3"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Subtotal</span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(drawer?.subtotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 font-semibold">
                      <span title={balanceTooltip} style={{ cursor: "help" }}>
                        {balanceLabel}
                      </span>
                    </span>
                    <span className="font-semibold text-slate-900">
                      {balanceDisplayAmount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-3xl border border-slate-200 bg-white shadow-xl p-6">
          <div className="text-xl md:text-2xl font-semibold text-slate-900">
            History
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className={`${labelText} uppercase`}>From</label>
              <input
                type="date"
                value={historyRange.start}
                onChange={(event) =>
                  setHistoryRange((prev) => ({
                    ...prev,
                    start: event.target.value,
                  }))
                }
                className={`${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className={`${labelText} uppercase`}>To</label>
              <input
                type="date"
                value={historyRange.end}
                onChange={(event) =>
                  setHistoryRange((prev) => ({
                    ...prev,
                    end: event.target.value,
                  }))
                }
                className={`${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
              />
            </div>
            <button
              type="button"
              onClick={handleHistorySearch}
              className={`${btnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
            >
              Search
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm md:text-base">
            <span className="text-slate-500">Quick links:</span>
            <button
              type="button"
              onClick={setTodayRange}
              className="text-rose-600 font-semibold hover:text-rose-700"
            >
              Today
            </button>
            <button
              type="button"
              onClick={setWeekRange}
              className="text-rose-600 font-semibold hover:text-rose-700"
            >
              This Week
            </button>
            <button
              type="button"
              onClick={setMonthRange}
              className="text-rose-600 font-semibold hover:text-rose-700"
            >
              This Month
            </button>
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-sm md:text-base font-semibold">
                    Date
                  </th>
                  <th className="px-4 py-3 text-sm md:text-base font-semibold">
                    Begin
                  </th>
                  <th className="px-4 py-3 text-sm md:text-base font-semibold">
                    Drawer
                  </th>
                </tr>
              </thead>
              <tbody>
                {(historyRows ?? []).length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-4 py-4 text-sm md:text-base text-slate-500"
                    >
                      No history found for this range.
                    </td>
                  </tr>
                ) : (
                  (historyRows ?? []).map((row) => (
                    <tr
                      key={row.work_date}
                      className="border-t border-slate-200 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={async () => {
                            const dateValue = row.work_date;
                            setViewingHistoryDate(dateValue);
                            setSelectedDate(dateValue);
                          }}
                          className="text-rose-600 font-semibold hover:text-rose-700"
                        >
                          {formatDisplayDate(row.work_date, salonTimezone)}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {formatMoney(row.begin_amount)}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {formatMoney(row.in_drawer_amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {beginOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4 py-6">
          <div
            role="dialog"
            aria-modal="true"
            className={`relative w-full max-w-sm md:max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden max-h-[calc(100vh-3rem)] flex flex-col ${modalText}`}
          >
            <div className="flex items-center justify-between bg-rose-500 px-5 py-3 shrink-0">
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                Begin Cash
              </h2>
              <ModalCloseX onClick={() => setBeginOpen(false)} />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <DenominationEditor
                denoms={beginDenoms}
                setDenoms={setBeginDenoms}
                readonlyTotal={Number(centsToMoney(beginDrawerCents))}
                showTotalRow={false}
                compact
              />
            </div>
            <div className="shrink-0 border-t border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between text-sm md:text-base font-semibold text-slate-900">
                <span>In Drawer</span>
                <span>{formatMoney(Number(centsToMoney(beginDrawerCents)))}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setBeginOpen(false)}
                  className={`${modalBtnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setBeginDenoms(buildEmptyDenoms())}
                  className={`${modalBtnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleUpdateBegin}
                  className={`${modalBtnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800`}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
